import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { appConfigApi } from '@/api/appConfig';
import { coursesApi } from '@/api/courses';
import { Card, CardTitle } from '@/components/ui/Card';
import { AI_SKILL_KEYS, aiSkillServerOn, nextCourseOff, type AiSkillKey } from '@/lib/aiSkillsAdmin';
import { humanizeError } from '@/lib/errors';

import { AiSkillSwitch } from '../AiSkillsServerCard';

/**
 * ИИ-задания курса — уровень курса (docs/CONTRACTS/ai-skills.md §4).
 *
 * Выключает навык только ученикам этого курса. Ученик видит навык, только если
 * он включён и здесь, и на сервере (раздел флагов); навык, выключенный
 * сервером, рисуется серым с подписью — щелчок здесь его не откроет, но запрет
 * курса хранится отдельно и сработает, когда сервер навык включит.
 *
 * Щелчок сохраняет сразу, список заменяется целиком.
 */
export function AiSkillsCard({ marathonId }: { marathonId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const settingsKey = ['admin', 'marathon', marathonId, 'settings'];
  const settings = useQuery({ queryKey: settingsKey, queryFn: () => coursesApi.getMarathonSettings(marathonId) });
  // Тот же ключ, что у страницы флагов, — уровень сервера для серой подписи.
  const config = useQuery({ queryKey: ['admin', 'app-config'], queryFn: appConfigApi.get });

  const off = settings.data?.ai_skills_off ?? [];
  const save = useMutation({
    mutationFn: ({ skill, next }: { skill: AiSkillKey; next: boolean }) =>
      coursesApi.setMarathonAISkills(marathonId, nextCourseOff(off, skill, next)),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKey }),
  });

  return (
    <Card>
      <CardTitle>{t('adminAiSkills.courseTitle')}</CardTitle>
      <p className="mb-3 text-sm text-on-surface-variant">{t('adminAiSkills.courseNote')}</p>
      <ul className="divide-y divide-outline-variant">
        {AI_SKILL_KEYS.map((skill) => {
          const on = !off.includes(skill);
          const serverOff = !aiSkillServerOn(config.data, skill);
          const busy = save.isPending && save.variables?.skill === skill;
          return (
            <li key={skill} className="flex items-center justify-between gap-4 py-3">
              <span className={serverOff ? 'text-sm text-on-surface-variant' : 'text-sm text-on-surface'}>
                {t(`adminAiSkills.skill.${skill}`)}
                {serverOff && config.data && (
                  <span className="ml-2 text-xs text-on-surface-variant">· {t('adminAiSkills.offByServer')}</span>
                )}
              </span>
              <AiSkillSwitch
                on={on}
                dimmed={serverOff}
                label={t(`adminAiSkills.skill.${skill}`)}
                disabled={busy || settings.isLoading}
                onToggle={() => save.mutate({ skill, next: !on })}
              />
            </li>
          );
        })}
      </ul>
      {(settings.isError || save.isError) && (
        <p className="mt-3 text-sm text-error">{humanizeError(settings.error ?? save.error)}</p>
      )}
    </Card>
  );
}
